import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as admin from '../../api/admin.ts';
import { toast } from '../../store/toast.ts';
import { Modal } from '../common/Modal.tsx';

/** 용량 선택지 (GB). 흔히 쓰는 값만 둔다. */
const QUOTAS = [10, 50, 100, 500, 1024, 2048];
const GB = 1024 ** 3;

/**
 * 사용자 추가 대화상자.
 *
 * 관리자가 계정을 직접 만드는 경로다. 가입을 닫아 둔(SIGNUP_ENABLED=false)
 * 운영에서는 이것이 유일한 계정 생성 수단이다.
 */
export function CreateUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [quota, setQuota] = useState(100);

  const create = useMutation({
    mutationFn: () => admin.createUser({
      email: email.trim(), password, display_name: name.trim(), role, quota_bytes: quota * GB,
    }),
    onSuccess: () => { toast.success('사용자를 만들었습니다'); onCreated(); },
    onError: (e: Error) => { toast.error(e); },
  });

  const valid = email.includes('@') && name.trim().length > 0 && password.length >= 10;

  return (
    <Modal
      title="사용자 추가"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>취소</button>
          <button
            className="btn btn--primary" disabled={!valid || create.isPending}
            onClick={() => { create.mutate(); }}
          >
            {create.isPending ? <span className="spinner" /> : '만들기'}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="nu-email">이메일</label>
        <input id="nu-email" className="input" type="email"
          value={email} onChange={(e) => { setEmail(e.target.value); }} />
      </div>
      <div className="field">
        <label htmlFor="nu-name">표시 이름</label>
        <input id="nu-name" className="input" maxLength={60}
          value={name} onChange={(e) => { setName(e.target.value); }} />
      </div>
      <div className="field">
        <label htmlFor="nu-pw">임시 비밀번호 (10자 이상)</label>
        <input id="nu-pw" className="input" type="text" autoComplete="off"
          value={password} onChange={(e) => { setPassword(e.target.value); }} />
        <span className="faint" style={{ fontSize: 12 }}>
          사용자에게 전달한 뒤 첫 로그인에서 바꾸도록 안내하세요.
        </span>
      </div>
      <div className="row">
        <div className="field spacer">
          <label htmlFor="nu-role">권한</label>
          <select id="nu-role" className="input" value={role}
            onChange={(e) => { setRole(e.target.value as 'user' | 'admin'); }}>
            <option value="user">일반 사용자</option>
            <option value="admin">관리자</option>
          </select>
        </div>
        <div className="field spacer">
          <label htmlFor="nu-quota">용량</label>
          <select id="nu-quota" className="input" value={String(quota)}
            onChange={(e) => { setQuota(Number(e.target.value)); }}>
            {QUOTAS.map((gb) => <option key={gb} value={gb}>{gb} GB</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
}
